import { Timestamp } from "firebase/firestore";

export interface OrgInfos {
  name: string;
  email: string;
  sender_email: string;

  phone_number: string;
  siret: string;
  city: string;
  postal: string;
  address: string;
  logo_url?: string;
}

/**
 * A garage closure period. `full=false` blocks online booking only (the atelier
 * planning stays normal); `full=true` is a full closure (also reddens the
 * planning). `annual=true` repeats every year (matched on MM-DD).
 */
export interface OrgClosure {
  id: string;
  /** Start date YYYY-MM-DD. */
  start: string;
  /** End date YYYY-MM-DD inclusive. Absent ⇒ single day. */
  end?: string;
  full?: boolean;
  annual?: boolean;
}

export interface OrgConfig {
  booking_min_days_ahead?: number;
  booking_max_days_ahead?: number;
  booking_max_per_day?: number;
  booking_safety_margin_hours?: number;
  booking_sr_min_hours_ahead?: number;
  booking_capacity_service_rapide?: number;
  booking_capacity_mecanique?: number;
  booking_capacity_diagnostic?: number;
  booking_capacity_carrosserie?: number;
  sr_reception_buffer_minutes?: number;
  sr_max_wait_minutes?: number;
  booking_opening_hour?: number;
  booking_closing_hour?: number;
  booking_lunch_start?: number;
  booking_lunch_end?: number;
  google_review_url?: string;
  /** Legacy: ISO dates (YYYY-MM-DD) on which the garage is closed. Superseded by booking_closures. */
  booking_closed_dates?: string[];
  /** Structured garage closures (online-booking block and/or full garage closure, optionally annual). */
  booking_closures?: OrgClosure[];
  /** When true, the booking widget lets clients pick a restitution date even for long interventions. */
  booking_ask_restitution_date_long?: boolean;
  devis_reminders?: {
    enabled?: boolean;
    r1_delay_hours?: number;
    r2_delay_hours?: number;
    r3_before_reception_hours?: number;
    cancel_before_reception_hours?: number;
    min_gap_hours?: number;
  };
}

export interface OrgWhatsApp {
  provider?: "wasender" | "twilio";
  twilio_phone_number?: string;
  wasender_api_key?: string;
  wasender_session_id?: string;
  phone_number?: string;
  business_name?: string;
  display_phone?: string;
  enabled?: boolean;
  connected?: boolean;
  solde?: number;
  total_spent?: number;
}

export interface OrgBridge {
  iban?: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  fees?: number;
  connected?: boolean;
}

export interface OrgServiceBoxSite {
  label: string;       // e.g. "PEUGEOT HERIC AUTOMOBILES"
  marque: string;      // e.g. "PEUGEOT"
  pdv_id: string;      // e.g. "448174E"
}

export interface OrgServiceBox {
  enabled?: boolean;
  connected?: boolean;
  username?: string;
  password?: string;
  site_code?: string;
  proxy_url?: string;
  proxy_version?: string;
  proxy_last_seen?: string;
  proxy_last_outage_start_at?: string;
  proxy_last_outage_end_at?: string;
  proxy_last_outage_duration_min?: number;
  sites?: OrgServiceBoxSite[];
}

export interface OrgEmailConfig {
  solde?: number;
  total_spent?: number;
  enabled?: boolean;
}

export interface OrgSignatureConfig {
  signature_level?: "simple" | "advanced";
  enabled?: boolean;
  total_spent?: number;
}

export interface OrgThreeCX {
  enabled?: boolean;
  connected?: boolean;
  server_url?: string;
  api_key?: string;
  webhook_secret?: string;
  user_extensions?: Record<string, string>;  // user_id -> extension number
}

/** Controls which online bookings auto-fill this column */
export type ReceptionBookingType = "reception" | "carrosserie";

/** Discriminator for the kind of column — drives which modal opens when clicking an RDV in this column */
export type ReceptionKind = "reception" | "location" | "pret" | "carrosserie" | "divers";

export interface OrgReception {
  id: string;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  barColor: string;
  user_id?: string;
  /** When set, online bookings of this type are distributed across columns with the same booking_type */
  booking_type?: ReceptionBookingType;
  /** Type de réception — when absent, derived from id prefix for backward compatibility */
  kind?: ReceptionKind;
  /** Linked ServiceBox réceptionnaire (agentreception). When set, transfers of RDV on this
   *  reception use this réceptionnaire instead of defaulting to the first one. */
  servicebox_receptionnaire_id?: string;
  servicebox_receptionnaire_name?: string;
}

/** Derives the reception kind from id prefix when `kind` is not set (backward compat). */
export function getReceptionKind(rec: Pick<OrgReception, "id" | "kind">): ReceptionKind {
  if (rec.kind) return rec.kind;
  if (rec.id.startsWith("location")) return "location";
  if (rec.id.startsWith("vehicule_pret") || rec.id.startsWith("pret")) return "pret";
  if (rec.id.startsWith("carrosserie")) return "carrosserie";
  if (rec.id.startsWith("rdv_divers") || rec.id.startsWith("divers")) return "divers";
  return "reception";
}

export const DEFAULT_RECEPTIONS: OrgReception[] = [
  { id: "rdv_divers",    label: "RDV Divers",       color: "#818CF8", bgColor: "#E0E6FF", borderColor: "#CCD0FF", barColor: "#A5B4FC", kind: "divers" },
  { id: "location_rac",  label: "Location",          color: "#5C6BFF", bgColor: "#CACFFF", borderColor: "#B7BDFF", barColor: "#818CF8", kind: "location" },
  { id: "vehicule_pret", label: "Véhicule de prêt", color: "#6F61FF", bgColor: "#D8D4FF", borderColor: "#C5BFFF", barColor: "#9B8FFC", kind: "pret" },
  { id: "carrosserie",   label: "Carrosserie",       color: "#8258FF", bgColor: "#DDD2FF", borderColor: "#CDBDFF", barColor: "#A78BFA", booking_type: "carrosserie", kind: "carrosserie" },
  { id: "reception_1",   label: "Réception 1",       color: "#CC5EFF", bgColor: "#EFD9FF", borderColor: "#E6C0FF", barColor: "#CC5EFF", booking_type: "reception", kind: "reception" },
  { id: "reception_2",   label: "Réception 2",       color: "#DF41FF", bgColor: "#EFC7FF", borderColor: "#E9B9FF", barColor: "#DF41FF", booking_type: "reception", kind: "reception" },
];

export interface Org {
  id: string;
  users_id: string[];
  creation_date: Timestamp;
  infos: OrgInfos;
  config?: OrgConfig;
  whatsapp?: OrgWhatsApp;
  bridge?: OrgBridge;
  servicebox?: OrgServiceBox;
  email_config?: OrgEmailConfig;
  signature_config?: OrgSignatureConfig;
  threecx?: OrgThreeCX;
  receptions?: OrgReception[];
  storage_used_bytes?: number;
}
